import { PusherService } from './../../../ng2/shared/services/pusher/pusher-service';
import * as angular from 'angular';
import * as _ from 'lodash';
import { IStudentMini } from '../../../ng2/shared/typings/interfaces/student.interface';
import { BulkUpdateMilestonesSuccess, CreateStudentSupportSuccess, LoadStudentSupports } from '../../../ng2/store';
import { sdcActionsConstant } from './sdc-actions.constant';

const CHANNELS = {
  SCHOOL_SUPPORTS: {
    ADDED: 'school-support-added',
    PATCHED: 'school-support-patched',
  },
  STUDENT_SUPPORTS: {
    ADDED: 'student-support-added',
    PATCHED: 'student-support-patched',
  },
};

angular
  .module('SdcFluxModule')
  .factory('SDCStoreSchoolChannel', (PusherService: PusherService, flux, ObjectCache, Store) => {
    return class SDCStoreSchoolChannel {
      private _schoolId: string;
      private _channel: any;

      constructor(schoolId) {
        const pusherChannelName = PusherService.getChannelName(schoolId);
        this._schoolId = schoolId;
        this._channel = PusherService.pusher.subscribe(pusherChannelName);

        this._onStudentsPatch(data => {
          flux.dispatch(sdcActionsConstant.MULTI_STUDENT_PATCH, {
            patches: data.patches,
            ignoreRecentlyUpdatedStudents: true,
          });
        });

        this._onStudentsDirty(data => {
          ObjectCache.getObject(data.cacheId).then(cachedObject => {
            flux.dispatch(sdcActionsConstant.MULTI_STUDENT_PATCH, { patches: [cachedObject] });
          });
        });

        this._onStudentSupportAdded((data: { itemToAdd; studentIdsToItemIds }) => {
          const cloned = _.cloneDeep(data);
          const { itemToAdd: studentSupport, studentIdsToItemIds } = cloned;
          const studentKey = 'student';
          const studentMini: IStudentMini = {
            studentId: null,
            lastFirst: null,
          };
          studentSupport[studentKey] = studentMini;
          flux.dispatch(sdcActionsConstant.ADD_STUDENT_SUPPORTS, {
            studentIdsToStudentSupportIds: studentIdsToItemIds,
            studentSupport,
          });

          // TODO: pusher message for `bulk` background jobs for students supports only includes a partial document.
          // Hence, we are reloading the student supports on bulk updates.
          // Once, we have a pattern to handle bulk updates, this should be refactored to follow that pattern (CM).

          // bulk pusher message does not include `student`.
          const isBulkUpdate = data.itemToAdd.student === undefined || data.itemToAdd.student.studentId === null;

          if (isBulkUpdate) {
            // since backgroundJob returns partial studentSupport doc, we reload
            Store.dispatch(new LoadStudentSupports({ schoolId: data.itemToAdd.schoolId }));

            // ideally we should dispatch BulkCreateStudentSupportsSuccess with a payload containing
            // the full studentSupport doc - but Pusher does not return full doc from the backend
            // so we have to populate the store by requerying the API for all studentSupports and
            // loading the full collection into ngrx

            // const storePayload = { studentSupport: data.itemToAdd };
            // Store.dispatch(new BulkCreateStudentSupportsSuccess(storePayload));'
          } else {
            Store.dispatch(new CreateStudentSupportSuccess(data.itemToAdd));
          }
        });

        this._onStudentSupportPatched(data => {
          const { patches } = data;
          _.each(patches, (item: any) => {
            const { patch, studentSupportIds } = item;
            flux.dispatch(sdcActionsConstant.UPDATE_STUDENT_SUPPORTS, {
              studentSupportIds,
              patch,
            });
          });
        });

        this._onGapPlanAdded(data => {
          const { gapPlan, studentIdsToGapPlanIds } = data;
          flux.dispatch(sdcActionsConstant.ADD_GAP_PLANS, {
            studentIdsToGapPlanIds,
            gapPlan,
          });
          flux.dispatch(sdcActionsConstant.RESET_FLATTENED_STUDENTS);
        });

        this._onGapPlanPatched(data => {
          const { patches } = data;
          _.each(patches, (patchObject: any) => {
            const { patch: updatedGapPlan } = patchObject;
            flux.dispatch(sdcActionsConstant.UPDATE_GAP_PLAN, updatedGapPlan);
            flux.dispatch(sdcActionsConstant.RESET_FLATTENED_STUDENTS);
          });
        });

        this._onCourseDiffAdded(data => {
          const { courseDiff, studentIdsToCourseDiffIds } = data;
          flux.dispatch(sdcActionsConstant.ADD_COURSE_DIFFS, {
            studentIdsToCourseDiffIds,
            courseDiff,
          });
          flux.dispatch(sdcActionsConstant.RESET_FLATTENED_STUDENTS);
        });

        this._onCourseDiffPatched(data => {
          const { patches } = data;
          _.each(patches, (patchObject: any) => {
            const { patch: updatedCourseDiff } = patchObject;
            flux.dispatch(sdcActionsConstant.UPDATE_COURSE_DIFF, updatedCourseDiff);
            flux.dispatch(sdcActionsConstant.RESET_FLATTENED_STUDENTS);
          });
        });

        this._onSchoolPatch(data => {
          const patches = data.patches;
          _.each(patches, (patchObject: any) => {
            const { patch } = patchObject;
            flux.dispatch(sdcActionsConstant.UPDATE_SCHOOL_BY_PATCH, { patch });
            flux.dispatch(sdcActionsConstant.RESET_FLATTENED_STUDENTS);
          });
        });

        this._onSchoolSupportAdded(data => {
          // Object keys should match keys found in the payload data received from the backend - JYR
          const { schoolSupport, schoolIdToSchoolSupportIds } = data;
          flux.dispatch(sdcActionsConstant.ADD_SCHOOL_SUPPORT, {
            schoolSupport,
            schoolIdToSchoolSupportIds,
          });
        });

        this._onSchoolSupportPatched(data => {
          _.each(data, singleSupport => {
            flux.dispatch(sdcActionsConstant.UPDATE_SCHOOL_SUPPORT, singleSupport);
          });
        });

        this._onMilestonePatch(data => {
          Store.dispatch(new BulkUpdateMilestonesSuccess(data.patches));
        });
      }

      /**
       * Registers an event handler for the `school-patch` event.
       *
       * @param handler {function} function that is called when the event happens, taking one arg: `data`
       * @return {function(): *} Unbind function
       */
      _onSchoolPatch(handler) {
        this._channel.bind('school-patch', handler);
        return () => this._channel.unbind('school-patch', handler);
      }

      /**
       * Registers an event handler for the `students-patch` event.
       *
       * @param handler {function} function that is called when the event happens, taking one arg: `data`
       * @return {function(): *} Unbind function
       */
      _onStudentsPatch(handler) {
        this._channel.bind('students-patch', handler);
        return () => this._channel.unbind('students-patch', handler);
      }

      /**
       * Registers an event handler for the `students-dirty` event.
       *
       * @param handler {function} function that is called when the event happens, taking one arg: `data`
       * @return {function(): *} Unbind function
       */
      _onStudentsDirty(handler) {
        this._channel.bind('students-dirty', handler);
        return () => this._channel.unbind('students-dirty', handler);
      }

      _onMilestonePatch(handler) {
        this._channel.bind('milestone-patched', handler);
        return () => this._channel.unbind('milestone-patched', handler);
      }

      /**
       * Registers an event handler for the `student-support-added` event.
       *
       * @param handler {function} function that is called when the event happens, taking one arg: `data`
       * @return {function(): *} Unbind function
       */
      _onStudentSupportAdded(handler) {
        this._channel.bind(CHANNELS.STUDENT_SUPPORTS.ADDED, handler);
        return () => this._channel.unbind(CHANNELS.STUDENT_SUPPORTS.ADDED, handler);
      }

      /**
       * Registers an event handler for the `student-support-patched` event.
       *
       * @param handler {function} function that is called when the event happens, taking one arg: `data`
       * @return {function(): *} Unbind function
       */
      _onStudentSupportPatched(handler) {
        this._channel.bind(CHANNELS.STUDENT_SUPPORTS.PATCHED, handler);
        return () => this._channel.unbind(CHANNELS.STUDENT_SUPPORTS.PATCHED, handler);
      }

      /**
       * Registers an event handler for the `gap-plan-added` event.
       *
       * @param handler {function} function that is called when the event happens, taking one arg: `data`
       * @return {function(): *} Unbind function
       */
      _onGapPlanAdded(handler) {
        this._channel.bind('gap-plan-added', handler);
        return () => this._channel.unbind('gap-plan-added', handler);
      }

      /**
       * Registers an event handler for the `gap-plan-added` event.
       *
       * @param handler {function} function that is called when the event happens, taking one arg: `data`
       * @return {function(): *} Unbind function
       */
      _onGapPlanPatched(handler) {
        this._channel.bind('gap-plan-patched', handler);
        return () => this._channel.unbind('gap-plan-patched', handler);
      }

      /**
       * Registers an event handler for the `course-diff-added` event.
       *
       * @param handler {function} function that is called when the event happens, taking one arg: `data`
       * @return {function(): *} Unbind function
       */
      _onCourseDiffAdded(handler) {
        this._channel.bind('course-diff-added', handler);
        return () => this._channel.unbind('course-diff-added', handler);
      }

      /**
       * Registers an event handler for the `course-diff-patched` event.
       *
       * @param handler {function} function that is called when the event happens, taking one arg: `data`
       * @return {function(): *} Unbind function
       */
      _onCourseDiffPatched(handler) {
        this._channel.bind('course-diff-patched', handler);
        return () => this._channel.unbind('course-diff-patched', handler);
      }

      /**
       * Registers an event handler for the `school-support-*` events.
       */
      _onSchoolSupportAdded(handler) {
        this._channel.bind(CHANNELS.SCHOOL_SUPPORTS.ADDED, handler);
        return () => this._channel.unbind(CHANNELS.SCHOOL_SUPPORTS.ADDED, handler);
      }

      _onSchoolSupportPatched(handler) {
        this._channel.bind(CHANNELS.SCHOOL_SUPPORTS.PATCHED, handler);
        return () => this._channel.unbind(CHANNELS.SCHOOL_SUPPORTS.PATCHED, handler);
      }

      /**
       * Disconnects from the pusher channel. Once this is called, no more events will be fired and no more handlers
       * will be allowed to be attached
       */
      destroy() {
        this._channel.unbind();
      }
    };
  });
